// frontend/src/site/templates/TemplateCorretorLovable/pages/SiteCorretorLovable.jsx
import React, { useEffect, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { useSite } from '@/site/SiteContext';

// Pages do template
import IndexPage from '@/site/templates/TemplateCorretorLovable/pages/Index';
import PropertiesPage from '@/site/templates/TemplateCorretorLovable/pages/Properties';
import PropertyDetailPage from '@/site/templates/TemplateCorretorLovable/pages/PropertyDetail';

// ✅ Catálogo (Firestore -> tenta siteCatalog e faz fallback em imoveis)
import useSiteCatalog from '@/site/hooks/useSiteCatalog';

/* =========================
   Helpers
========================= */
function useRouteMode() {
  const { pathname } = useLocation();
  const p = String(pathname || '').toLowerCase();

  const isCatalog = p.includes('/imoveis');
  const isDetail = p.includes('/imovel/');

  return { isCatalog, isDetail };
}

function safeSlug(v) {
  return String(v || '')
    .trim()
    .toLowerCase();
}

function asObj(v) {
  return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
}
function asArr(v) {
  return Array.isArray(v) ? v : [];
}

function getFeaturedIds(conteudo) {
  const c = asObj(conteudo);
  const im = asObj(c.imoveis);
  const des = asObj(c.destaques);

  const idsIm = asArr(im.ids).filter(Boolean).map(String);
  const idsDes = asArr(des.ids).filter(Boolean).map(String);

  return idsIm.length ? idsIm : idsDes;
}

export default function SiteCorretorLovable() {
  const params = useParams();
  const slug = safeSlug(params.slug || params.handle);

  const { search } = useLocation();
  const { isCatalog, isDetail } = useRouteMode();

  const siteCtx = useSite() || {};
  const {
    loading,
    isReady,
    perfilSite,
    ownerId,
    ownerWhatsUrl,
    conteudo,
    themeVars,
  } = siteCtx;

  const notFound = useMemo(() => !loading && !ownerId, [loading, ownerId]);

  const filtros = useMemo(() => {
    const sp = new URLSearchParams(search);
    return {
      q: sp.get('q') || '',
      tipo: sp.get('tipo') || '',
      cidade: sp.get('cidade') || '',
      bairro: sp.get('bairro') || '',
      estado: sp.get('estado') || '',
      dorm: sp.get('dorm') || '',
      min: sp.get('min') || '',
      max: sp.get('max') || '',
      ref: sp.get('ref') || '',
    };
  }, [search]);

  const featuredIds = useMemo(() => getFeaturedIds(conteudo), [conteudo]);

  const hitsPerPage = isCatalog
    ? 24
    : Math.max(6, Math.min(9, featuredIds.length || 6));

  const mode = isCatalog ? 'catalog' : 'home';

  const {
    docs: properties,
    load,
    loading: loadingProps,
    done,
  } = useSiteCatalog(ownerId, filtros, hitsPerPage, {
    mode,
    ids: featuredIds,
  });

  const siteConfig = useMemo(() => {
    const p = perfilSite || {};
    const c = conteudo || {};

    return {
      uid: ownerId || null,
      handle: slug,
      siteEnabled: true,

      displayName: p?.nome || p?.displayName || 'Corretor',
      nome: p?.nome || p?.displayName || 'Corretor',
      creci: p?.creci || '',
      whatsapp: p?.whatsappDisplay || p?.telefone || '',
      whatsappUrl: ownerWhatsUrl || '',
      fotoUrl: p?.fotoPerfilUrl || p?.fotoUrl || p?.photoURL || '',
      bio: c?.sobre?.bio || p?.bio || '',
      cidade: p?.cidade || '',
      estado: p?.estado || '',

      instagram: p?.instagramUrl || p?.instagram || '',
      especialidades: p?.especialidades || [],

      builder: c,
      themeVars: themeVars || {},
      template: 'TemplateCorretorLovable',
      raw: p,
    };
  }, [perfilSite, conteudo, ownerId, slug, ownerWhatsUrl, themeVars]);

  const propsLoading = Boolean(loadingProps && (properties?.length || 0) === 0);

  useEffect(() => {
    if (!isReady) return;
    if (!ownerId) return;

    load({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, ownerId, isCatalog, search]);

  /* =========================
     🎨 THEME WRAPPER
  ========================= */

  const Shell = ({ children }) => {
    const theme = String(themeVars?.theme || 'bege').toLowerCase();
    const style = String(themeVars?.style || 'solid').toLowerCase();
    const texture = String(themeVars?.texture || 'off').toLowerCase();

    return (
      <div
        className="min-h-screen"
        data-theme={theme}
        data-style={style}
        data-texture={texture}
      >
        {children}
      </div>
    );
  };

  /* ========================= */

  if (notFound) {
    return (
      <Shell>
        <div className="flex min-h-screen items-center justify-center p-6">
          <div className="max-w-md w-full rounded-2xl border border-border bg-card p-6 text-center">
            <h1 className="text-xl font-semibold text-foreground">
              Site não encontrado
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              Esse corretor não tem site ativo ou o link está incorreto.
            </p>
          </div>
        </div>
      </Shell>
    );
  }

  if (loading && !perfilSite) {
    return (
      <Shell>
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-muted-foreground text-sm">Carregando…</div>
        </div>
      </Shell>
    );
  }

  if (isDetail) {
    return (
      <Shell>
        <PropertyDetailPage
          siteConfig={siteConfig}
          properties={properties}
          slug={slug}
          handle={slug}
          conteudo={conteudo}
          loading={propsLoading}
        />
      </Shell>
    );
  }

  if (isCatalog) {
    return (
      <Shell>
        <PropertiesPage
          siteConfig={siteConfig}
          properties={properties}
          slug={slug}
          handle={slug}
          conteudo={conteudo}
          loading={propsLoading}
          done={done}
          onLoadMore={load}
        />
      </Shell>
    );
  }

  return (
    <Shell>
      <IndexPage
        siteConfig={siteConfig}
        properties={properties}
        slug={slug}
        handle={slug}
        conteudo={conteudo}
        loading={propsLoading}
      />
    </Shell>
  );
}
